import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { BeatLoader } from 'react-spinners';
import { formatString } from '../../utils';
import { FaEdit } from 'react-icons/fa';
import { MdOutlineSwapHorizontalCircle } from "react-icons/md";
import ModalComponent from '../atoms/Modal/ModalComponent/ModalComponent';
import HorseForm from '../../pages/HorseAdmin/HorseForm';
import { useMutation } from "@apollo/client";
import { UPDATE_HORSE } from "../../apollo/mutations/horses";
import { createToast } from "../../utils/Toast";
import ThreeDots from '../../assets/icons/more-vertical.svg';
import ActionButton from '../Common/ActionButton';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const Toast = createToast();

function HorsesTable({ horses, loading, sortConfig, handleSort, onEdit, onChangeOwnership }) {
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
  const [isOwnershipModalOpen, setIsOwnershipModalOpen] = useState(false);
  const [updateHorse, { loading: updateHorseLoading }] = useMutation(UPDATE_HORSE);
  const editHorseFormRef = useRef(null);
  const ownershipHorseFormRef = useRef(null);

  const handleEditClick = (horse) => {
    setSelectedHorse(horse);
    setIsEditModalOpen(true);
  };

  const handleOwnershipClick = (horse) => {
    setSelectedHorse(horse);
    setIsOwnershipModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedHorse(null);
  };

  const closeOwnershipModal = () => {
    setIsOwnershipModalOpen(false);
    setSelectedHorse(null);
  };

  const handleUpdateHorseSubmit = async (formData) => {
    await updateHorse({
      variables: {
        input: {
          id: formData.id,
          name: formData.horseName,
          dob: formData.date,
        },
      },
      refetchQueries: ['listHorses']
    })
      .then(response => {
        if (response?.data.updateHorse) {
          Toast.fire({
            icon: 'success',
            title: 'Horse Updated Successfully',
          });
          closeEditModal();
        }
      })
      .catch(error => {
        Toast.fire({
          icon: 'error',
          title: error.message || 'Error updating horse',
        });
      });
  };

  const handleOwnershipSubmit = async (formData) => {
    await updateHorse({
      variables: {
        input: {
          id: formData.id,
          changeOwnership: true,
          email: formData.email,
        },
      },
      refetchQueries: ['listHorses']
    })
      .then(response => {
        if (response?.data.updateHorse) {
          Toast.fire({
            icon: 'success',
            title: 'Ownership Verified Successfully',
          });
          closeOwnershipModal();
        }
      })
      .catch(error => {
        Toast.fire({
          icon: 'error',
          title: error.message || 'Error updating ownership',
        });
      });
  };

  const handleModalUpdate = () => {
    if (editHorseFormRef.current) {
      editHorseFormRef.current.submit();
    }
  };

  const handleModalOwnershipUpdate = () => {
    if (ownershipHorseFormRef.current) {
      ownershipHorseFormRef.current.submit();
    }
  };

  const sortedHorses = React.useMemo(() => {
    if (sortConfig.key && horses?.length) {
      return [...horses].sort((a, b) => {
        const getValue = (horse) => {
          switch (sortConfig.key) {
            case 'name':
              return horse?.name ?? '';
            case 'dob':
              return horse?.dob ?? '';
            case 'owner':
              return horse?.owner?.name ?? '';
            case 'gulletWidth':
              return horse?.measurements?.gulletWidth ?? '';
            case 'barAngle':
              return horse?.measurements?.barAngle ?? '';
            case 'discipline':
              return horse?.discipline?.[0] ?? '';
            case 'status':
              return horse?.status ?? '';
            default:
              return '';
          }
        };

        const aValue = getValue(a);
        const bValue = getValue(b);
        const isString = typeof aValue === 'string' || typeof bValue === 'string';

        if (isString) {
          return sortConfig.direction === 'ascending'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        } else {
          return sortConfig.direction === 'ascending'
            ? aValue - bValue
            : bValue - aValue;
        }
      });
    }
    return horses;
  }, [horses, sortConfig]);

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <BeatLoader color={'#2b364b'} />
      </div>
    );
  }

  if (!horses || horses?.length === 0) {
    return (
      <div className="flex justify-center items-center mt-8">
        <p className="admin-dashboard text-3xl text-gray-500">
          No Horses Found. Please check back later.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-full  overflow-hidden"
        style={{
          border: '1px solid #999',
          borderRadius: '10px',
          borderSpacing: '0',
        }}
      >
        <thead className="thead">
          <tr className="table-head">
            <th
              scope="col"
              align="left"
              className="pl-6 pr-3 py-2 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('name')}
            >
              Name
              <span className="text-sm ml-1">
                {sortConfig.key === 'name'
                  ? sortConfig.direction === 'ascending'
                    ? '↑'
                    : '↓'
                  : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
              </span>
            </th>
            <th
              scope="col"
              align="left"
              className="px-3 py-2 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('dob')}
            >
              Date of Birth
              <span className="text-sm ml-1">
                {sortConfig.key === 'dob'
                  ? sortConfig.direction === 'ascending'
                    ? '↑'
                    : '↓'
                  : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
              </span>
            </th>
            <th
              scope="col"
              align="left"
              className="px-3 py-2 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('owner')}
            >
              Owner
              <span className="text-sm ml-1">
                {sortConfig.key === 'owner'
                  ? sortConfig.direction === 'ascending'
                    ? '↑'
                    : '↓'
                  : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
              </span>
            </th>
            <th
              scope="col"
              align="center"
              className="px-3 py-2 group text-center"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('gulletWidth')}
            >
              Gullet Width
              <span className="text-sm ml-1">
                {sortConfig.key === 'gulletWidth'
                  ? sortConfig.direction === 'ascending'
                    ? '↑'
                    : '↓'
                  : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
              </span>
            </th>
            <th
              scope="col"
              align="center"
              className="px-3 py-2 group text-center"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('barAngle')}
            >
              Bar Angle
              <span className="text-sm ml-1">
                {sortConfig.key === 'barAngle'
                  ? sortConfig.direction === 'ascending'
                    ? '↑'
                    : '↓'
                  : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
              </span>
            </th>
            <th
              scope="col"
              align="left"
              className="px-3 py-2 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('discipline')}
            >
              Discipline
              <span className="text-sm ml-1">
                {sortConfig.key === 'discipline'
                  ? sortConfig.direction === 'ascending'
                    ? '↑'
                    : '↓'
                  : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
              </span>
            </th>
            <th
              scope="col"
              align="left"
              className="pl-3 pr-6 py-2 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('status')}
            >
              Status
              <span className="text-sm ml-1">
                {sortConfig.key === 'status'
                  ? sortConfig.direction === 'ascending'
                    ? '↑'
                    : '↓'
                  : (
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      ↑
                    </span>
                  )}
              </span>
            </th>
            <th
              scope="col"
              align="center"
              className="px-3 py-2 group"
              style={{ borderBottom: '1px solid #999' }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedHorses?.map((horse, index) => (
            <tr key={index}>
              <td
                className="pl-6 pr-3 py-2 whitespace-nowrap"
                style={{ borderBottom: '1px solid #999' }}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 serial-number flex items-center h-10 w-auto overflow-hidden text-ellipsis whitespace-nowrap">
                    {formatString(horse.name) || 'N/A'}
                  </div>
                </div>
              </td>
              <td
                align="start"
                className="table-date px-3 py-2 whitespace-nowrap"
                style={{ borderBottom: '1px solid #999' }}
              >
                {horse.dob ? dayjs(horse.dob).format('MMM Do YYYY') : 'N/A'}
              </td>
              <td
                align="start"
                className="table-date px-3 py-2 whitespace-nowrap"
                style={{ borderBottom: '1px solid #999' }}
              >
                {formatString(horse?.owner?.name) || 'N/A'}
              </td>
              <td
                align="center"
                className="px-3 py-2 whitespace-nowrap table-order text-center"
                style={{ borderBottom: '1px solid #999' }}
              >
                {horse?.measurements?.gulletWidth?.toFixed(2) || 'N/A'}
              </td>
              <td
                align="center"
                className="px-3 py-2 whitespace-nowrap table-order text-center"
                style={{ borderBottom: '1px solid #999' }}
              >
                {horse?.measurements?.barAngle?.toFixed(2) || 'N/A'}
              </td>
              <td
                align="start"
                className="table-buyer px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal"
                style={{ borderBottom: '1px solid #999' }}
              >
                {formatString(horse?.discipline?.[0]) || 'N/A'}
              </td>
              <td
                align="start"
                className="pl-3 pr-6 py-2 whitespace-nowrap table-order !text-[#000] !font-normal"
                style={{ borderBottom: '1px solid #999' }}
              >
                {formatString(horse.status) || 'N/A'}
              </td>
              <td
                align="center"
                className="px-3 py-2 whitespace-nowrap"
                style={{ borderBottom: '1px solid #999' }}
              >

                <ActionButton
                  icon={ThreeDots}
                  index={index}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  actions={[
                    {
                      label: 'Edit Horse',
                      icon: FaEdit,
                      onClick: () => handleEditClick(horse),
                    },
                    {
                      label: 'Ownership',
                      icon: MdOutlineSwapHorizontalCircle,
                      onClick: () => handleOwnershipClick(horse),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isEditModalOpen && (
        <ModalComponent
          title="Edit Horse"
          openEditModal={isEditModalOpen}
          onClose={closeEditModal}
          setOpenEditModal={setIsEditModalOpen}
          setOpenModal={setIsEditModalOpen}
          setCancelModal={setIsEditModalOpen}
          onUpdate={handleModalUpdate}
          updateLoading={updateHorseLoading}
        >
          <HorseForm
            ref={editHorseFormRef}
            title="Update Horse"
            onSubmit={handleUpdateHorseSubmit}
            loading={updateHorseLoading}
            selectedHorse={selectedHorse}
          />
        </ModalComponent>
      )}

      {isOwnershipModalOpen && (
        <ModalComponent
          title="Change Horse Ownership"
          onClose={closeOwnershipModal}
          setOpenEditModal={setIsOwnershipModalOpen}
          setOpenModal={setIsOwnershipModalOpen}
          setCancelModal={setIsOwnershipModalOpen}
          onCreate={handleModalOwnershipUpdate}
          createLoading={updateHorseLoading}
        >
          <HorseForm
            ref={ownershipHorseFormRef}
            title="Update Horse Ownership"
            onSubmit={handleOwnershipSubmit}
            loading={updateHorseLoading}
            selectedHorse={selectedHorse}
          />
        </ModalComponent>
      )}
    </div>
  );
}

export default HorsesTable;
